<section class="bg-star-gray-600 pt-[40px] pb-[56px] lg:pt-[64px] lg:pb-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <p class="text-[13px] uppercase tracking-[1.6px] text-star-green-500">
      Solutions for exchanges
    </p>
    <h1
      class="mt-[14px] max-w-[760px] text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[42px]"
    >
      AML Screening and Monitoring for Crypto Exchanges
    </h1>
    <p class="mt-[20px] max-w-[720px] text-[16px] leading-[1.8] text-white/80">
      An exchange is where money from outside the platform becomes money on the platform's books.
      AMLOfficer puts a screening layer on that boundary: every incoming address checked against
      four AML data providers at once, every wallet you keep worth watching kept under watch, and
      the result delivered where your desk already works — the dashboard, your team's Telegram
      group, or your own back office through the API.
    </p>
    <div class="mt-[28px] flex flex-col gap-[14px] sm:flex-row sm:items-center">
      <a
        [href]="createAccountHref()"
        target="_blank"
        class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
      >
        <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
        <mat-icon
          svgIcon="second-arrow-right"
          class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
        ></mat-icon>
      </a>
    </div>
    <p class="mt-[16px] max-w-[560px] text-[14px] leading-[1.6] text-white/50">
      Exchanges work from a business account, which is where wallets, team access and the API keys
      live. Commercial terms follow your volumes and appear in the account once you register.
    </p>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Volume is what makes exchange compliance different
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Most compliance tooling is built around a person looking at an address. An exchange does not
      have that luxury. Deposits arrive continuously from addresses nobody at the company has ever
      seen, they are credited in seconds, and by the time anything looks wrong the balance has
      already been traded, mixed with the balances of other users and, often, withdrawn. The
      decision has to be made at the moment of arrival or it is not really a decision at all.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      The same asymmetry works on the way out. A withdrawal is the last moment an exchange controls
      where funds go, and it is the point a regulator or a partner bank will ask about later. What
      an exchange needs is not more analysis per address — it is the same analysis applied to every
      address, automatically, with a human pulled in only where the answer is not obvious.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Four points in an exchange flow where an address gets decided on
    </h2>
    <div class="mt-[32px] grid grid-cols-1 gap-[20px] md:grid-cols-2">
      <div class="rounded-[16px] bg-star-gray-650 p-[24px] shadow-inner-white lg:p-[28px]">
        <span class="text-[13px] font-medium tracking-[1.2px] text-star-green-500">01</span>
        <h3 class="mt-[10px] text-[19px] font-medium leading-[1.35] text-white">
          As the deposit lands
        </h3>
        <p class="mt-[12px] text-[15px] leading-[1.75] text-white/70">
          The sender is read at the moment of arrival, while the balance is not yet spendable. This
          is the check that separates a deposit you credit from one you hold, and the one that
          decides whether an analyst ever needs to see it.
        </p>
      </div>
      <div class="rounded-[16px] bg-star-gray-650 p-[24px] shadow-inner-white lg:p-[28px]">
        <span class="text-[13px] font-medium tracking-[1.2px] text-star-green-500">02</span>
        <h3 class="mt-[10px] text-[19px] font-medium leading-[1.35] text-white">
          Before a withdrawal leaves
        </h3>
        <p class="mt-[12px] text-[15px] leading-[1.75] text-white/70">
          The destination address is screened before the payout is signed. An exchange only gets one
          opportunity to ask where its users are sending funds, and the answer is far cheaper before
          the transaction than after it.
        </p>
      </div>
      <div class="rounded-[16px] bg-star-gray-650 p-[24px] shadow-inner-white lg:p-[28px]">
        <span class="text-[13px] font-medium tracking-[1.2px] text-star-green-500">03</span>
        <h3 class="mt-[10px] text-[19px] font-medium leading-[1.35] text-white">
          When a counterparty is onboarded
        </h3>
        <p class="mt-[12px] text-[15px] leading-[1.75] text-white/70">
          Liquidity partners, corporate clients and other venues bring their own history with them.
          Screening the addresses they trade from turns onboarding due diligence into something
          written down rather than something assumed.
        </p>
      </div>
      <div class="rounded-[16px] bg-star-gray-650 p-[24px] shadow-inner-white lg:p-[28px]">
        <span class="text-[13px] font-medium tracking-[1.2px] text-star-green-500">04</span>
        <h3 class="mt-[10px] text-[19px] font-medium leading-[1.35] text-white">
          For as long as you hold the wallet
        </h3>
        <p class="mt-[12px] text-[15px] leading-[1.75] text-white/70">
          Addresses that matter to the exchange — hot wallets, settlement wallets, the addresses of
          large clients — stay under continuous monitoring, so a change in what they are exposed to
          reaches your team instead of waiting for the next manual review.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Screening inside the pipeline, not beside it
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      An exchange that has to copy an address into a separate tool will check some of them. An
      exchange whose deposit processor calls a screening endpoint checks all of them. AMLOfficer is
      built for the second case: the API lets the check sit in the code path that credits a deposit
      or releases a withdrawal, and the answer comes back as part of that flow.
    </p>
    <div class="mt-[28px] grid grid-cols-1 gap-[18px] md:grid-cols-3">
      <div class="border-t border-solid border-white/15 pt-[18px]">
        <h3 class="text-[17px] font-medium leading-[1.4] text-white">Fast API</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Reports land in the dashboard, where the compliance team picks them up and works on them
          together.
        </p>
      </div>
      <div class="border-t border-solid border-white/15 pt-[18px]">
        <h3 class="text-[17px] font-medium leading-[1.4] text-white">Pro API</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          AML data goes straight into your own systems in real time, for the cases where the
          exchange wants full control of what happens next.
        </p>
      </div>
      <div class="border-t border-solid border-white/15 pt-[18px]">
        <h3 class="text-[17px] font-medium leading-[1.4] text-white">Manual tools</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          The same checks run by hand in the web interface, for the address that arrived outside the
          usual flow and needs a person to look at it now.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px] lg:flex lg:items-center lg:gap-[48px]">
    <div class="lg:flex-[1.4]">
      <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
        Four providers behind one deposit decision
      </h2>
      <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
        AMLOfficer aggregates four AML providers, including BitOK, Global Ledger and Beosin. The
        point of aggregation is disagreement: providers draw on different datasets, and an address
        one of them calls clean is regularly flagged by another.
      </p>
      <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
        For an exchange that difference is operational, not academic. A single-source check that
        misses an address does not produce a warning — it produces a credited deposit and a
        conversation months later. Asking several providers at once is the cheapest way to stop one
        vendor's blind spot from becoming the exchange's problem.
      </p>
    </div>
    <div class="mt-[32px] flex justify-center lg:mt-0 lg:flex-1">
      <img
        src="./images/aml-providers.webp"
        alt="AML provider reports combined in the AMLOfficer interface"
        width="337"
        height="213"
        class="w-full max-w-[337px] object-contain"
        loading="lazy"
      />
    </div>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Built for a desk that works in shifts
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Exchange compliance is rarely one person. It is a rota, a queue and a handover, and the tool
      has to survive all three. Business accounts support role-based access and shared
      investigations, so an alert raised on the night shift is picked up in the morning with its
      history intact rather than re-investigated from scratch.
    </p>
    <ul class="mt-[24px] flex flex-col gap-[14px]">
      <li class="flex gap-[12px] text-[16px] leading-[1.75] text-white/80">
        <span class="mt-[9px] h-[6px] w-[6px] shrink-0 rounded-full bg-star-green-500"></span>
        Invite the people who need access, give them roles, and keep the case they are working on in
        one place instead of in a chat thread.
      </li>
      <li class="flex gap-[12px] text-[16px] leading-[1.75] text-white/80">
        <span class="mt-[9px] h-[6px] w-[6px] shrink-0 rounded-full bg-star-green-500"></span>
        Real-time alerts on monitored wallets reach the team the moment something changes — in the
        dashboard or as a Telegram notification to the group already watching the desk.
      </li>
      <li class="flex gap-[12px] text-[16px] leading-[1.75] text-white/80">
        <span class="mt-[9px] h-[6px] w-[6px] shrink-0 rounded-full bg-star-green-500"></span>
        Reports stay attached to the address, which is what makes a formal request answerable months
        later without reconstructing who decided what.
      </li>
    </ul>
  </div>
</section>

<section class="bg-star-gray-600 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      When a deposit turns out to be someone else's stolen funds
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Exchanges receive the proceeds of other people's incidents more often than any other kind of
      crypto business, and screening is only the first half of that story. AMLOfficer is a brand of
      StarCompliance, whose team works on recovering stolen assets as well as tracking them — so an
      exchange that finds itself holding funds from an incident has somewhere to take the case.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      That side of the work starts with a conversation rather than a form.
      <a [routerLink]="'/contact-us'" class="text-white underline underline-offset-4">
        Contact the team
      </a>
      if that is the situation you are in.
    </p>
  </div>
</section>

<section class="bg-star-gray-650 py-[56px] lg:py-[72px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Exchange compliance: common questions
    </h2>
    <div class="mt-[28px] flex flex-col gap-[24px]">
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Can screening run on every deposit automatically?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Yes. That is what the API is for: the deposit processor calls it, the answer comes back in
          the same flow, and only the cases that need a person reach one. The same checks are also
          available manually in the dashboard when someone wants to look at an address directly.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          How is this different from screening a wallet once?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          A check describes an address at the moment you ask. Monitoring keeps asking. An exchange
          needs both: the check decides whether to credit the deposit, and monitoring covers the
          addresses that stay important to you after that decision.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          Do several colleagues work in the same account?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          Yes. A business account is built for teams: you invite colleagues, assign roles and share
          investigations, rather than passing one login around the desk.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">Where do alerts arrive?</h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          In the dashboard, and in Telegram for the teams that live there — wallet reports and
          alerts both work from a Telegram group, which is usually where an exchange's duty channel
          already is.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-medium leading-[1.4] text-white">
          What does it cost for an exchange?
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.75] text-white/70">
          There is no public tariff table. Commercial terms scale with volume and become visible in
          the account once the exchange registers.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pt-[56px] pb-[72px] lg:pt-[72px] lg:pb-[96px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <div
      class="rounded-[20px] bg-star-gray-650 px-[24px] py-[36px] shadow-inner-white lg:px-[48px] lg:py-[48px]"
    >
      <h2 class="max-w-[620px] text-[24px] font-medium leading-[1.3] text-white lg:text-[30px]">
        Put your deposit and withdrawal flow behind one screening layer
      </h2>
      <p class="mt-[16px] max-w-[620px] text-[16px] leading-[1.75] text-white/70">
        Open a business account to add your first wallets, invite your compliance team and see how
        the checks fit into the flow you already run.
      </p>
      <div class="mt-[28px] flex flex-col gap-[16px] sm:flex-row sm:items-center">
        <a
          [href]="createAccountHref()"
          target="_blank"
          class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
        >
          <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
          <mat-icon
            svgIcon="second-arrow-right"
            class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
          ></mat-icon>
        </a>
        <a
          [routerLink]="'/contact-us'"
          class="text-[16px] leading-[1.6] text-white/70 underline underline-offset-4 hover:text-white"
        >
          Or talk to the team about your setup
        </a>
      </div>
    </div>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
